import { ThreadEditorVMConfig } from 'components/editors/thread/thread.interfaces';
import CommentsView from 'enums/comments/views';
import { wccModules } from 'enums/wccModules';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import { IWCCStorageManager } from 'managers/iStorage';
import TaskManager from 'managers/task';
import { TopicManager } from 'managers/topic';
import CommentsManager from 'managers/topic/comments';
import VideoPresentationProgressManager from 'managers/videoPresentationProgress';
import { withEffects } from 'mixins/withEffects';
import { Person } from 'models/person';
import Task from 'models/task';
import { Topic } from 'models/topic';
import { TopicThread } from 'models/topicThread';
import { createExtendedContainerUsingEffects } from '../../configuration/inversify/container/extended';
import { ActivityFormConfig, ActivityFormViewConfig } from './form.common';
import activityFormsInfoSets from './form.formsConfig';
import activityFormsPreviewInfoSets from './form.previewsConfig';

export interface ActivityFormEditorConfig extends ThreadEditorVMConfig {
    componentId: string
}

type ActivityFormConfigsSet = { forms: Array<ActivityFormConfig>, overrides?: Partial<ThreadEditorVMConfig> }

const activityFormsSet = {
    form: <ActivityFormConfigsSet>{ forms: activityFormsInfoSets },
    preview: <ActivityFormConfigsSet>{ forms: activityFormsPreviewInfoSets, overrides: { preview: true } }
}

type ActivityFormType = keyof typeof activityFormsSet

interface ActivityFormManagerOptions {
    thread?: SubscribableOrNullableValue<TopicThread>
    isMobile?: SubscribableOrNullableValue<boolean>
    toolsContainer?: SubscribableOrNullableValue<HTMLElement>
}

export default class ActivityFormManager {
    private effects = withEffects();

    private topicId: Subscribable<string | undefined>
    private toolsContainer: Subscribable<HTMLElement | undefined>

    private user: Subscribable<Person | undefined>
    private task: Subscribable<Task | undefined>
    private topic: Subscribable<Topic | undefined>
    private thread: Subscribable<TopicThread | undefined>

    private userComments: Subscribable<Array<TopicThread>>

    private isVPFinished: Subscribable<boolean>
    private isMobile: Subscribable<boolean>
    private areUserCommentsLoaded: Subscribable<boolean>

    ready: Subscribable<boolean>

    constructor(
        taskId: SubscribableOrNullableValue<string>,
        topicId: SubscribableOrNullableValue<string>,
        options?: ActivityFormManagerOptions
    ) {
        this.topicId = ko.flattenComputed(topicId);
        this.thread = ko.flattenComputed(options?.thread);
        this.isMobile = ko.flattenComputed(options?.isMobile, false);
        this.toolsContainer = ko.flattenComputed(options?.toolsContainer);

        const container = createExtendedContainerUsingEffects(this.effects);
        const storage = <IWCCStorageManager>container.get(wccModules.storage);

        const userManager = storage.get(CurrentUserManager, { discussionId: taskId });
        this.user = userManager.pluck(m => m.person);

        const taskManager = storage.get(TaskManager, { taskId });
        this.task = taskManager.pluck(m => m.task);

        const topicManager = storage.get(TopicManager, { topicId });
        this.topic = topicManager.pluck(m => m.topic);

        const userCommentsManager = storage.get(CommentsManager, { topicId, view: CommentsView.mine });
        this.userComments = userCommentsManager.pluck(m => m.realComments, []);
        const isLoadingComments = userCommentsManager.pluck(m => m.loading).extend({ deferred: true });
        this.areUserCommentsLoaded = isLoadingComments.not();

        const vpProgressManager = storage.get(VideoPresentationProgressManager, { topicId });
        this.isVPFinished = vpProgressManager.pluck(m => m.isFinished, false);

        this.ready = ko.pureComputed(() => this.user() != undefined && this.task() != undefined && this.topic() != undefined);
    }

    getEditorConfig(type: ActivityFormType) {
        const { forms, overrides = {} } = activityFormsSet[type];

        const formViewConfig = ko.pureComputed(() => this.getFormViewConfig(forms)).extend({ deferred: true });
        const formEditorConfig = ko.pureComputed(() => this.getFormEditorConfig(formViewConfig(), overrides)).extend({ deferred: true });

        return formEditorConfig;
    }

    dispose() {
        this.effects.dispose();
    }

    private getFormViewConfig(formConfigs: Array<ActivityFormConfig>) {
        const task = this.task();
        const topic = this.topic();
        const user = this.user();
        const thread = this.thread();

        if (task != undefined && topic != undefined && user != undefined) {
            const type = this.isMobile() ? 'mobile' : 'classic';
            const canAddComment = this.checkIfCanAddComment(topic, user);

            const set = _(formConfigs).find(set => set.isActive({ task, topic, user, canAddComment, thread }));

            if (set != undefined)
                return set.views[type] ?? set.views.classic;
        }
    }

    private getFormEditorConfig(formViewConfig: ActivityFormViewConfig | undefined, overrides: Partial<ThreadEditorVMConfig> = {}) {
        if (formViewConfig != undefined) {
            let componentId: string, config: ThreadEditorVMConfig;

            if (_.isString(formViewConfig)) {
                componentId = formViewConfig;
                config = {};
            } else {
                componentId = formViewConfig[0];
                config = formViewConfig[1];
            }

            return <ActivityFormEditorConfig>{
                ...config,
                ...overrides,

                componentId,
                topicId: this.topicId,
                thread: this.thread,
                toolsContainer: this.toolsContainer
            }
        }
    }

    private checkIfCanAddComment(topic: Topic, user: Person) {
        if (!user.isRegular())
            return true;

        if (topic.isVideoPresentation())
            return !this.isVPFinished();

        var commentsLimit = topic.commentsLimit();

        if (commentsLimit === 0) {
            return true;
        } else if (commentsLimit === 1) {
            return !topic.isAnswered();
        } else {
            const comments = this.userComments();
            const areCommentsReady = this.areUserCommentsLoaded();

            if (areCommentsReady)
                return comments.length < commentsLimit;
        }

        return false;
    }
}